/*
  Konflikt-Erkennung für den Schichtplan V2.

  Ein Konflikt liegt vor, wenn im sichtbaren Zeitraum
  - sich zwei Schichten desselben Mitarbeitenden zeitlich überlappen,
  - eine Schicht mit einer erfassten Absenz kollidiert oder
  - eine Schicht in die Ferien eines Mitarbeitenden fällt.

  Für Absenz- und Verfügbarkeitsprüfungen zählt eine Schicht nur an ihrem
  Starttag: Läuft sie über Mitternacht (z. B. 17:00–01:00), verletzt der Teil
  nach Mitternacht keine Absenz/Verfügbarkeit des Folgetags. Schicht-gegen-
  Schicht-Überlappungen bleiben davon unberührt (physisch echte Kollision).
*/

import { parseDateValue, toIsoDate } from "../date/month";
import { getAvailabilitySummaryForRange } from "./availability";
import { getUserHolidayDayKeys } from "./userHolidays";

export const CONFLICT_TYPES = Object.freeze({
  SHIFT_OVERLAP: "shift_overlap",
  SHIFT_ON_ABSENCE: "shift_on_absence",
  SHIFT_ON_HOLIDAY: "shift_on_holiday",
  SHIFT_AGAINST_AVAILABILITY: "shift_against_availability",
});

const CONFLICT_TYPE_LABELS = Object.freeze({
  [CONFLICT_TYPES.SHIFT_OVERLAP]: "Überlappende Schichten",
  [CONFLICT_TYPES.SHIFT_ON_ABSENCE]: "Schicht trotz Absenz",
  [CONFLICT_TYPES.SHIFT_ON_HOLIDAY]: "Schicht während Ferien",
  [CONFLICT_TYPES.SHIFT_AGAINST_AVAILABILITY]: "Schicht ausserhalb Verfügbarkeit",
});

export function getConflictTypeLabel(type) {
  return CONFLICT_TYPE_LABELS[type] || "Konflikt";
}

function isWorkShiftEntry(entry) {
  return entry?.entryType === "shift" || entry?.entryType === "custom_shift";
}

function isAbsenceEntry(entry) {
  return entry?.entryType === "absence";
}

function getEntryInterval(entry) {
  const start = parseDateValue(entry?.start);
  const end = parseDateValue(entry?.end);
  if (!start || !end || end <= start) return null;
  return { start: start.getTime(), end: end.getTime() };
}

function intervalsOverlap(left, right) {
  return left.start < right.end && right.start < left.end;
}

export function clampShiftEndToStartDay(start, end) {
  const startDate = parseDateValue(start);
  const endDate = parseDateValue(end);
  if (!startDate || !endDate) return endDate;

  const nextMidnight = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
  return endDate <= nextMidnight ? endDate : nextMidnight;
}

function getStartDayInterval(entry) {
  const interval = getEntryInterval(entry);
  if (!interval) return null;

  const clampedEnd = clampShiftEndToStartDay(entry?.start, entry?.end);
  if (!clampedEnd) return interval;
  return { start: interval.start, end: Math.min(interval.end, clampedEnd.getTime()) };
}

function formatEntryLabel(entry) {
  const title = String(entry?.title || "").trim() || "Eintrag";
  const timeLabel = String(entry?.timeLabel || "").trim();
  return timeLabel ? `${title} (${timeLabel})` : title;
}

function buildVisibleDayKeySet(days) {
  return new Set((Array.isArray(days) ? days : []).map((day) => String(day?.key || "")).filter(Boolean));
}

function isEntryInVisibleDays(entry, visibleDayKeys) {
  if (!visibleDayKeys.size) return true;

  const startKey = toIsoDate(entry?.start) || String(entry?.dateKey || "");
  const endKey = toIsoDate(entry?.end) || startKey;
  if (!startKey) return false;

  // Mehrtägige Einträge zählen, sobald ein Tag im Zeitraum liegt.
  for (const dayKey of visibleDayKeys) {
    if (dayKey >= startKey && dayKey <= endKey) return true;
  }
  return false;
}

function compareConflicts(left, right) {
  const dateCompare = String(left.dateKey).localeCompare(String(right.dateKey));
  if (dateCompare !== 0) return dateCompare;

  const nameCompare = String(left.employeeName).localeCompare(String(right.employeeName), "de", {
    sensitivity: "base",
  });
  if (nameCompare !== 0) return nameCompare;
  return String(left.id).localeCompare(String(right.id));
}

function groupEntriesByEmployee(entries) {
  return entries.reduce((accumulator, entry) => {
    const employeeId = String(entry?.employeeId || "");
    if (!employeeId) return accumulator;
    if (!accumulator.has(employeeId)) accumulator.set(employeeId, []);
    accumulator.get(employeeId).push(entry);
    return accumulator;
  }, new Map());
}

function buildOverlapConflicts(workShiftsByEmployee) {
  const conflicts = [];

  workShiftsByEmployee.forEach((shifts) => {
    const timedShifts = shifts
      .map((shift) => ({ shift, interval: getEntryInterval(shift) }))
      .filter((item) => item.interval)
      .sort((left, right) => left.interval.start - right.interval.start);

    for (let index = 0; index < timedShifts.length; index += 1) {
      const current = timedShifts[index];

      for (let cursor = index + 1; cursor < timedShifts.length; cursor += 1) {
        const other = timedShifts[cursor];
        if (other.interval.start >= current.interval.end) break;
        if (!intervalsOverlap(current.interval, other.interval)) continue;

        conflicts.push({
          id: `overlap:${current.shift.id}:${other.shift.id}`,
          type: CONFLICT_TYPES.SHIFT_OVERLAP,
          employeeId: String(current.shift.employeeId),
          employeeName: current.shift.employeeName || "",
          dateKey: String(current.shift.dateKey || toIsoDate(current.shift.start) || ""),
          entryIds: [String(current.shift.id), String(other.shift.id)],
          detail: `${formatEntryLabel(current.shift)} überschneidet sich mit ${formatEntryLabel(other.shift)}.`,
        });
      }
    }
  });

  return conflicts;
}

function buildAbsenceConflicts(workShiftsByEmployee, absencesByEmployee) {
  const conflicts = [];

  workShiftsByEmployee.forEach((shifts, employeeId) => {
    const absences = (absencesByEmployee.get(employeeId) || [])
      .map((absence) => ({ absence, interval: getEntryInterval(absence) }))
      .filter((item) => item.interval);
    if (!absences.length) return;

    shifts.forEach((shift) => {
      const shiftInterval = getStartDayInterval(shift);
      if (!shiftInterval) return;

      absences.forEach(({ absence, interval }) => {
        if (!intervalsOverlap(shiftInterval, interval)) return;

        conflicts.push({
          id: `absence:${shift.id}:${absence.id}`,
          type: CONFLICT_TYPES.SHIFT_ON_ABSENCE,
          employeeId: String(shift.employeeId),
          employeeName: shift.employeeName || "",
          dateKey: String(shift.dateKey || toIsoDate(shift.start) || ""),
          entryIds: [String(shift.id), String(absence.id)],
          detail: `${formatEntryLabel(shift)} kollidiert mit der Absenz "${String(absence.title || "Absenz").trim()}".`,
        });
      });
    });
  });

  return conflicts;
}

function buildHolidayConflicts(workShiftsByEmployee, userHolidays) {
  const holidayDaysByEmployee = new Map();

  (Array.isArray(userHolidays) ? userHolidays : []).forEach((holiday) => {
    const employeeId = String(holiday?.userId || "");
    if (!employeeId) return;
    if (!holidayDaysByEmployee.has(employeeId)) holidayDaysByEmployee.set(employeeId, new Map());

    const dayMap = holidayDaysByEmployee.get(employeeId);
    getUserHolidayDayKeys(holiday).forEach((dayKey) => {
      if (!dayMap.has(dayKey)) dayMap.set(dayKey, holiday);
    });
  });

  const conflicts = [];
  workShiftsByEmployee.forEach((shifts, employeeId) => {
    const holidayDays = holidayDaysByEmployee.get(employeeId);
    if (!holidayDays?.size) return;

    shifts.forEach((shift) => {
      const dateKey = String(shift.dateKey || toIsoDate(shift.start) || "");
      const holiday = holidayDays.get(dateKey);
      if (!holiday) return;

      const holidayName = String(holiday.holidayTypeName || "").trim() || "Ferien";
      conflicts.push({
        id: `holiday:${shift.id}:${holiday.id}`,
        type: CONFLICT_TYPES.SHIFT_ON_HOLIDAY,
        employeeId: String(shift.employeeId),
        employeeName: shift.employeeName || "",
        dateKey,
        entryIds: [String(shift.id), String(holiday.id)],
        detail: `${formatEntryLabel(shift)} fällt in die Ferien "${holidayName}".`,
      });
    });
  });

  return conflicts;
}

function employeeHasAvailabilityData(employeeId, availabilityEntries, availabilityDefaults) {
  const matches = (record) => String(record?.userId || "") === employeeId;
  return (
    (Array.isArray(availabilityEntries) ? availabilityEntries : []).some(matches) ||
    (Array.isArray(availabilityDefaults) ? availabilityDefaults : []).some(matches)
  );
}

/*
  Prüft jede Arbeitsschicht gegen das Verfügbarkeitssystem: Fällt sie (teilweise)
  in eine erfasste Absenz oder ausserhalb der hinterlegten Verfügbarkeit, ist das
  ein Konflikt. `alreadyFlaggedShiftIds` verhindert doppelte Absenz-Meldungen, wenn
  dieselbe Schicht schon über einen Kalender-Absenzeintrag erkannt wurde.
*/
function buildAvailabilityConflicts(workShiftsByEmployee, availabilityContext, alreadyFlaggedShiftIds) {
  const { availabilityEntries, availabilityDefaults, companyAvailabilityMode, userAvailabilityModes } =
    availabilityContext;

  const hasAnyData =
    (Array.isArray(availabilityEntries) && availabilityEntries.length > 0) ||
    (Array.isArray(availabilityDefaults) && availabilityDefaults.length > 0);
  if (!hasAnyData) return [];

  const conflicts = [];

  workShiftsByEmployee.forEach((shifts, employeeId) => {
    shifts.forEach((shift) => {
      if (!getEntryInterval(shift)) return;

      const summary = getAvailabilitySummaryForRange({
        employeeId,
        start: shift.start,
        end: clampShiftEndToStartDay(shift.start, shift.end),
        availabilityEntries,
        availabilityDefaults,
        companyAvailabilityMode,
        userAvailabilityModes,
      });

      // "available" = die Schicht liegt vollständig in verfügbarer Zeit → kein Konflikt.
      if (!summary || summary.status === "available") return;

      const isAvailabilityViolation = summary.kind === "availability";

      // Verfügbarkeits-Modus: Ohne hinterlegte Verfügbarkeit gibt es nichts zu
      // verletzen — sonst würde jede Schicht nicht-erfassender MA fälschlich melden.
      if (isAvailabilityViolation && !employeeHasAvailabilityData(employeeId, availabilityEntries, availabilityDefaults)) {
        return;
      }

      // Absenz aus dem Verfügbarkeitssystem nicht doppelt zum Kalender-Eintrag melden.
      if (!isAvailabilityViolation && alreadyFlaggedShiftIds.has(String(shift.id))) return;

      const type = isAvailabilityViolation
        ? CONFLICT_TYPES.SHIFT_AGAINST_AVAILABILITY
        : CONFLICT_TYPES.SHIFT_ON_ABSENCE;
      const detail = isAvailabilityViolation
        ? `${formatEntryLabel(shift)} liegt ausserhalb der hinterlegten Verfügbarkeit.`
        : `${formatEntryLabel(shift)} kollidiert mit einer erfassten Absenz.`;

      conflicts.push({
        id: `availability:${shift.id}`,
        type,
        employeeId: String(shift.employeeId),
        employeeName: shift.employeeName || "",
        dateKey: String(shift.dateKey || toIsoDate(shift.start) || ""),
        entryIds: [String(shift.id)],
        detail,
      });
    });
  });

  return conflicts;
}

export function buildConflicts({
  shifts,
  userHolidays,
  days,
  availabilityEntries = [],
  availabilityDefaults = [],
  companyAvailabilityMode = "absence",
  userAvailabilityModes = {},
} = {}) {
  const visibleDayKeys = buildVisibleDayKeySet(days);
  const visibleEntries = (Array.isArray(shifts) ? shifts : []).filter(
    (entry) => entry?.id && isEntryInVisibleDays(entry, visibleDayKeys)
  );
  const visibleHolidays = (Array.isArray(userHolidays) ? userHolidays : []).filter((holiday) =>
    getUserHolidayDayKeys(holiday).some((dayKey) => !visibleDayKeys.size || visibleDayKeys.has(dayKey))
  );

  const workShiftsByEmployee = groupEntriesByEmployee(visibleEntries.filter(isWorkShiftEntry));
  const absencesByEmployee = groupEntriesByEmployee(visibleEntries.filter(isAbsenceEntry));

  const absenceConflicts = buildAbsenceConflicts(workShiftsByEmployee, absencesByEmployee);
  const absenceFlaggedShiftIds = new Set(
    absenceConflicts.map((conflict) => String(conflict.entryIds?.[0] || "")).filter(Boolean)
  );

  const conflicts = [
    ...buildOverlapConflicts(workShiftsByEmployee),
    ...absenceConflicts,
    ...buildHolidayConflicts(workShiftsByEmployee, visibleHolidays),
    ...buildAvailabilityConflicts(
      workShiftsByEmployee,
      { availabilityEntries, availabilityDefaults, companyAvailabilityMode, userAvailabilityModes },
      absenceFlaggedShiftIds
    ),
  ].sort(compareConflicts);

  const countsByType = conflicts.reduce((accumulator, conflict) => {
    accumulator[conflict.type] = (accumulator[conflict.type] || 0) + 1;
    return accumulator;
  }, {});

  return {
    conflicts,
    totalCount: conflicts.length,
    countsByType,
  };
}
